import {createAsyncThunk} from '@reduxjs/toolkit';

import {TPredicateExpressionDto} from 'features/predicate-expression-form-list';
import {ILookupEntry} from 'shared/types/lookups';
import {TAppState} from 'store/config/types';

import {fetchTableReportFilterConditions} from '../dd-lookups/dd-lookups-api';
import {selectTableReportConfigurationFilterConditions} from '../dd-lookups/dd-lookups-selectors';
import {REPORT_CONFIGURATION_SLICE} from '../report-configuration-constants';
import {
    IDeleteDdRestrictionsByIdArgs,
    IFetchDdRestrictionsByIdArgs,
    IPostDdRestrictionsByIdArgs,
    deleteDdRestrictionsById,
    fetchDdRestrictionsById,
    postDdRestrictionsById,
} from './dd-restrictions-configuration-api';

export interface ILoadDdRestrictionData {
    ddRestrictionsExpressionDto: TPredicateExpressionDto | undefined;
    tableReportFilterLookups: ILookupEntry[];
}

export interface ILoadDdRestrictionArgs extends IFetchDdRestrictionsByIdArgs {
    templateCode: string | null | undefined;
}

export const loadDdRestrictions = createAsyncThunk<ILoadDdRestrictionData, ILoadDdRestrictionArgs>(
    `${REPORT_CONFIGURATION_SLICE}/loadDdRestrictions`, async (args, {rejectWithValue, getState}) => {
        try {
            const tableReportFiltersInStore = selectTableReportConfigurationFilterConditions(getState() as TAppState);

            const fetchDdRestrictions = async () => {
                try {
                    const fetchResult = await fetchDdRestrictionsById(args);
                    return fetchResult;
                } catch (e) {
                    if (e.response.status === 404) return {data: undefined};
                    throw new Error(e);
                }
            };

            const loadRequests = await Promise.all([
                await fetchDdRestrictions(),
                await (tableReportFiltersInStore ? Promise.resolve(
                    {data: tableReportFiltersInStore},
                ) : fetchTableReportFilterConditions()),
            ]);

            const [
                ddRestrictionsExpressionDtoResponse,
                tableReportRestrictionsResponse,
            ] = loadRequests;

            return {
                ddRestrictionsExpressionDto: ddRestrictionsExpressionDtoResponse?.data,
                tableReportFilterLookups: tableReportRestrictionsResponse?.data,
            };
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateDdRestrictions = createAsyncThunk<string, IPostDdRestrictionsByIdArgs>(
    `${REPORT_CONFIGURATION_SLICE}/updateDdRestrictions`, async (args, {rejectWithValue}) => {
        try {
            const {data} = await postDdRestrictionsById(args);
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const deleteDdRestrictions = createAsyncThunk<string, IDeleteDdRestrictionsByIdArgs>(
    `${REPORT_CONFIGURATION_SLICE}/deleteDdRestrictions`, async (args, {rejectWithValue}) => {
        try {
            const {data} = await deleteDdRestrictionsById(args);
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
